import React, { useState } from 'react'
import { Typography, Button, message, Alert } from 'antd'
import { useTranslation } from 'react-i18next'
import ModalWrapper from './ModalWrapper'
import { BasicModalProps } from '../../types/modals'
import { CitySelector, City } from '../Common/CitySelector'
import { useCities } from '../../hooks/useCities'
import { useApiQuery } from '../../hooks/useApiQuery'
import './Modals.css'
import './JoinCommunityModal.css'

const { Title, Paragraph } = Typography

interface CityWithWhatsApp extends City {
    whatsappGroupLink: string
}

export const JoinCommunityModal: React.FC<BasicModalProps> = ({
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation()
    const [selectedCityId, setSelectedCityId] = useState<string>('')
    const [selectedCity, setSelectedCity] = useState<City | null>(null)
    const [isLoadingWhatsApp, setIsLoadingWhatsApp] = useState(false)
    const { cities, error: citiesError } = useCities() // Use shared cities hook

    const { refetch: fetchWhatsAppLink } = useApiQuery<CityWithWhatsApp>({
        endpoint: selectedCityId ? `website/cities/${selectedCityId}` : '',
        method: 'GET',
        enabled: false,
        queryKey: ['city-whatsapp', selectedCityId],
    })

    const handleCityChange = (cityId: string) => {
        setSelectedCityId(cityId)
        // Find city from the cities we fetched (not from CitySelector's internal state)
        const city = cities.find((c) => c.cityId === cityId)
        setSelectedCity(city || null)
    }

    const handleJoinClick = async () => {
        if (!selectedCityId || !selectedCity) {
            message.warning(t('modals.joinCommunity.selectCityFirst'))
            return
        }

        // Check if WhatsApp link is already in the city data
        if (selectedCity.whatsappGroupLink) {
            window.open(
                selectedCity.whatsappGroupLink,
                '_blank',
                'noopener,noreferrer'
            )
            onClose()
            return
        }

        // If not in city data, fetch it from the API via useApiQuery
        setIsLoadingWhatsApp(true)
        try {
            const data = await fetchWhatsAppLink()

            const whatsappLink = data?.whatsappGroupLink

            if (!whatsappLink) {
                message.error(t('modals.joinCommunity.whatsappLinkNotAvailable'))
                return
            }

            // Open WhatsApp link in a new tab
            window.open(whatsappLink, '_blank', 'noopener,noreferrer')

            // Close modal after opening link
            onClose()
        } catch (error) {
            console.error('Error fetching WhatsApp link:', error)
            message.error(t('modals.joinCommunity.failedToGetLink'))
        } finally {
            setIsLoadingWhatsApp(false)
        }
    }

    return (
        <ModalWrapper isOpen={isOpen} onClose={onClose} width={600}>
            <div className="modal-content join-community-modal">
                <div className="join-community-container">
                    <Title className="title" level={2}>
                        {t('modals.joinCommunity.title')}
                    </Title>
                    <Paragraph className="description">
                        {t('modals.joinCommunity.description')}
                    </Paragraph>

                    {citiesError && (
                        <Alert
                            message={t('modals.joinCommunity.unableToLoadCities')}
                            description={
                                citiesError.message ||
                                t('modals.joinCommunity.failedToFetchCities')
                            }
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <div className="city-selector">
                        <CitySelector
                            placeholder={t('common.selectCity')}
                            value={selectedCityId}
                            onChange={handleCityChange}
                            size="large"
                            className="city-select"
                        />
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        onClick={handleJoinClick}
                        loading={isLoadingWhatsApp}
                        disabled={!selectedCityId}
                        className="join-button"
                    >
                        {isLoadingWhatsApp
                            ? t('modals.joinCommunity.loading')
                            : t('modals.joinCommunity.joinButton')}
                    </Button>
                </div>
            </div>
        </ModalWrapper>
    )
}
